import { type PropsWithChildren, type RefObject } from 'react';
import { useTranslation } from 'next-i18next/pages';
import cn from 'clsx';
import { cva } from 'class-variance-authority';
import { getUploadcareImageTransformationProps } from '@pkgs/components/utils/getUploadcareImageTransformationProps';
import { AiBadge, type TFunction } from '@pkgs/components/index';
import { Visibility } from '@pkgs/components/components/Visibility/Visibility';
import { formatShopPhoneNumber } from '@pkgs/api/utils/strings';
import { DEFAULT_IMAGE, getUploadCareImageUrl, type Listing, ImageAiType } from '@pkgs/api';
import { Headline } from '@ev-tech/ui-core-components/Headline';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';

import { getParsedShopName } from '@/utils/propertyDataUtils';
import { getAiBadgeTranslationKey } from '@/utils/aiBadgeUtils';
import { PageVariant } from '@/types/expose';
import { useExposeModal } from '@/providers/ExposeModalProvider';
import { PhoneIcon } from '@/icons/small/Phone';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useShouldHideUserAccountExclusiveContent } from '@/hooks/useShouldHideUserAccountExclusiveContent';
import { useListingPageComponentRefs } from '@/hooks/useListingPageComponentRefs';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';
import { PRIVATE_OFFICE_IMAGE_ROSE } from '@/consts/image';
import { Link } from '@/components/property-expose/Link';
import { ImageComponent } from '@/components/common/ImageComponent/ImageComponent';
import { MailBoldIcon } from '@/components/common/Icons/MailBoldIcon';

import { PrivateOfficeAdvisorBadge } from './PrivateOfficeAdvisorBadge';
import { PhoneLinkWrapper } from '../PhoneLinkWrapper/PhoneLinkWrapper';
import { ContactButtonsGroup } from '../ContactButtonsGroup/ContactButtonsGroup';

import styles from './AgentShopCard.module.css';

export type AgentShopCardProps = {
  isEmbedded?: boolean;
};

type AgentShopCardContentProps = AgentShopCardProps & {
  buttonRef?: RefObject<HTMLButtonElement>;
};

const mailButtonVariants = cva(styles.mailButton, {
  variants: {
    hasPhoneButton: {
      true: styles.mailButtonHasPhone,
      false: undefined,
    },
    isEmbedded: {
      true: styles.mailButtonEmbedded,
      false: undefined,
    },
  },
});

const MaybeLink = ({ href, className, children }: PropsWithChildren<{ href?: string; className?: string }>) =>
  href ? (
    <a href={href} className={cn(styles.link, className)} target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : (
    children
  );

const ExposeAgentShopCard = ({ buttonRef, listing }: AgentShopCardContentProps & { listing: Listing }) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);

  const phoneNumbers = [listing.shopPhoneNumber, listing.agent?.mobile]
    .filter<string>((i): i is string => Boolean(i))
    .map(formatShopPhoneNumber);

  const {
    agent,
    displayId,
    isDevelopmentProject,
    shopName,
    shopProfilePicture,
    shopProfilePictureUploadcareId,
    shopProfilePictureAiGenerated,
  } = listing;
  const shopImageSource = shopProfilePictureUploadcareId
    ? getUploadCareImageUrl({ imageId: shopProfilePictureUploadcareId })
    : (shopProfilePicture ?? DEFAULT_IMAGE);

  const prefixedShopname = `Engel & Völkers ${getParsedShopName(shopName ?? '')}`;

  const displayIdKey = isDevelopmentProject
    ? 'search.expose.shopCard.label.projectIdPrefix'
    : 'search.expose.shopCard.label.propertyIdPrefix';

  return (
    <div className={styles.container} data-testid="agent-shop-contact-card">
      <div className={styles.imageContainer}>
        <ImageComponent
          className={styles.image}
          src={shopImageSource}
          height={96}
          width={96}
          alt="engel & völkers shop image"
          data-testid="agent-shop-card_shop-image"
          {...getUploadcareImageTransformationProps({ aspectRatio: '1/1', fit: 'cover' })}
        />
        {shopProfilePictureAiGenerated && (
          <AiBadge
            aiType={ImageAiType.MODIFIED}
            t={t as TFunction}
            translationKey={getAiBadgeTranslationKey({ size: 's' })}
            size="s"
            offset="s"
            isPrivateOffice={listing.isPrivateOffice}
            side="center"
          />
        )}
      </div>
      <div className={styles.textContainer}>
        <Headline className={styles.headline} label={prefixedShopname} size="h5" />
        {phoneNumbers.length > 0 && (
          <div className={styles.phoneNumbersContainer}>
            {phoneNumbers.map((p, index) => (
              <PhoneLinkWrapper key={index} trigger="agent-shop-card" index={index} phoneNumberCount={phoneNumbers.length}>
                <Link className={styles.phoneNumber} label={p} href={'tel:' + p} />
              </PhoneLinkWrapper>
            ))}
          </div>
        )}
        <Visibility visibleFrom="laptop">
          <ContactButtonsGroup
            buttonGroupClassName={styles.buttonsContainer}
            overlineClassName={styles.overline}
            buttonRef={buttonRef}
            testId="agent-shop-card_primary_request-expose"
            shouldShowStroke={true}
          />
        </Visibility>
        <div className={styles.agentInfoContainer}>
          {!!agent?.name && (
            <Copy
              text={`${t('search.expose.shopCard.label.agentNamePrefix')}: ${agent.name}${agent.registrationNumber ? `, ${agent.registrationNumber}` : ''}`}
              size="m"
            />
          )}
          {Boolean(displayId) && <Copy className={styles.displayId} text={`${t(displayIdKey)}: ${displayId}`} size="m" />}
        </div>
      </div>
    </div>
  );
};

const PrivateOfficeAgentShopCard = ({ buttonRef, isEmbedded }: AgentShopCardContentProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { onContactModalTriggerClick } = useExposeModal();
  const {
    data: { listing, genericListing },
  } = useListing();

  const { agent, displayId } = genericListing;
  const agentProfileImageSource = agent?.profilePictureUploadcareId
    ? getUploadCareImageUrl({ imageId: agent.profilePictureUploadcareId })
    : (agent?.profilePicture ?? PRIVATE_OFFICE_IMAGE_ROSE);
  const hasAgentProfileImage = Boolean(agent?.profilePictureUploadcareId || agent?.profilePicture);

  return (
    <div className={styles.privateOfficeContainer} data-testid="agent-shop-contact-card-private-office" data-embedded={isEmbedded}>
      <div className={styles.privateOfficeOuterContainer}>
        <PrivateOfficeAdvisorBadge isAmbassador={agent?.privateOfficeAmbassador} />
        <div className={styles.privateOfficeInnerContainer}>
          <MaybeLink href={agent?.profileUrl}>
            <ImageComponent
              className={styles.image}
              src={agentProfileImageSource}
              height={96}
              width={96}
              alt="engel & völkers agent profile image"
              data-private-office-image
              data-embedded={isEmbedded}
              data-has-padding={!hasAgentProfileImage}
              {...getUploadcareImageTransformationProps({ aspectRatio: '1/1', fit: 'cover' })}
            />
          </MaybeLink>
          <div className={styles.columnContainer}>
            {!!agent?.name && (
              <div className={styles.agentNameContainer}>
                {!!agent.academicTitle && <Copy className={styles.jobTitle} text={agent.academicTitle} />}
                <MaybeLink href={agent.profileUrl}>
                  <Headline className={styles.agentName} label={agent.name} size="h5" data-testid="test" />
                </MaybeLink>
              </div>
            )}

            {!!agent?.jobTitle && <Copy className={styles.jobTitle} text={agent.jobTitle} />}
            {!!agent?.selfEmployed && <Copy className={styles.jobBadge} text={t('search.request.access.form.selfEmployed.label')} />}
            {listing && (
              <Visibility visibleFrom="laptop" className={styles.requestAccessButtonVisibilityWrapper}>
                <Button
                  className={cn(mailButtonVariants({ hasPhoneButton: !!agent?.mobile, isEmbedded }))}
                  ref={buttonRef}
                  onClick={onContactModalTriggerClick}
                  testId="agent-shop-card_primary_request-expose"
                  label={t('search.expose.shopCard.label.contactButton')}
                  icon={<MailBoldIcon />}
                  size="medium"
                />
              </Visibility>
            )}
            {!!agent?.mobile && (
              <div className={styles.requestAccessButtonWrapper} data-testid="agent-mobile-section" data-embedded={isEmbedded}>
                <Visibility visibleFrom="laptop" className={styles.requestAccessButtonVisibilityWrapper}>
                  <Button
                    as="a"
                    testId="agent-shop-card_primary_request-access"
                    label={agent.mobile}
                    icon={<PhoneIcon />}
                    size="medium"
                    variant="tertiary"
                    href={`tel:${agent.mobile.replaceAll(/\s/g, '')}`}
                    className={styles.requestAccessButtonVisibilityWrapper}
                  />
                </Visibility>
                <Copy
                  className={styles.copy}
                  size="l"
                  text={<Link className={styles.phoneLink} href={`tel:${agent.mobile.replaceAll(/\s/g, '')}`} label={agent.mobile} />}
                />
              </div>
            )}

            {Boolean(displayId) && (
              <Copy className={styles.displayId} text={`${t('search.expose.shopCard.label.propertyIdPrefix')}: ${displayId}`} size="m" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AgentShopCard = ({ isEmbedded }: AgentShopCardProps) => {
  const { agentCardCtaRef } = useListingPageComponentRefs();
  const { pageVariant } = useExposePageProps();
  const { data } = useListing();
  const isUAExclusiveListingAndNotSignedIn = useShouldHideUserAccountExclusiveContent();
  const { listing, restrictedListing } = data;
  const shouldHideAgentShopCard = (restrictedListing && !isEmbedded) || isUAExclusiveListingAndNotSignedIn;

  if (shouldHideAgentShopCard) {
    return null;
  }
  if (
    pageVariant === PageVariant.PRIVATE_OFFICE_PUBLIC ||
    pageVariant === PageVariant.PRIVATE_OFFICE_RESTRICTED ||
    pageVariant === PageVariant.PRIVATE_OFFICE_RESTRICTED_WITH_PASSWORD_PROTECTION ||
    pageVariant === PageVariant.PRIVATE_OFFICE_RESTRICTED_WITHOUT_PASSWORD_PROTECTION ||
    restrictedListing
  ) {
    return <PrivateOfficeAgentShopCard buttonRef={agentCardCtaRef} isEmbedded={isEmbedded} />;
  }

  if (!listing) {
    return null;
  }

  return <ExposeAgentShopCard buttonRef={agentCardCtaRef} listing={listing} />;
};
